// client/src/components/StreamCard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { getJSON } from "../lib/api";

function getMuxThumb(playbackId) {
  if (!playbackId) return null;
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?time=5&fit_mode=pad&width=640&height=360`;
}

function formatLongDate(value) {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function StreamCard({ channel }) {
  const navigate = useNavigate();

  const safeChannel = channel || {};
  const slugOrId = safeChannel.slug || safeChannel.id || "";

  const [meta, setMeta] = React.useState(null);

  React.useEffect(() => {
    let alive = true;

    async function load() {
      if (!slugOrId) {
        if (alive) setMeta(null);
        return;
      }

      const res = await getJSON(`/streams/meta/${encodeURIComponent(slugOrId)}`);
      if (!alive) return;

      if (res.ok && res.data?.meta) {
        setMeta(res.data.meta);
      } else {
        setMeta(null);
      }
    }

    load();
    const t = setInterval(load, 15000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [slugOrId]);

  if (!channel) return null;

  const { id, name, isLive, playbackId, lastVodPlaybackId } = channel;

  const displayName = meta && meta.title ? meta.title : name || id;

  const lastLiveAt =
    channel.lastLiveAt ||
    channel.lastVodCreatedAt ||
    channel.latestVodCreatedAt ||
    channel.lastStreamedAt ||
    null;
  const lastLiveText = formatLongDate(lastLiveAt);

  const vodId = lastVodPlaybackId || channel.lastVodPlaybackID || channel.lastVod || null;
  const liveId = playbackId || channel.livePlaybackId || null;
  const thumbId = liveId || vodId;
  const muxThumb = getMuxThumb(thumbId);

  return (
    <>
      <div className="streamCard">
        <span className={`streamCard__badge ${isLive ? "live" : "idle"}`}>
          {isLive ? "LIVE" : "IDLE"}
        </span>

        <h3 className="streamCard__title">{displayName}</h3>

        <p className="streamCard__status">
          {isLive ? "Live now" : lastLiveText ? `Last live ${lastLiveText}` : "No sessions yet"}
        </p>

        <div className="streamCard__thumb">
          {muxThumb ? (
            <>
              <img
                src={muxThumb}
                alt="Latest stream thumbnail"
                className="streamCard__thumbImg"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="streamCard__thumbLabel">
                {isLive ? "LIVE" : vodId ? "Latest VOD" : "No video yet"}
              </div>
            </>
          ) : (
            <span style={{ opacity: 0.75 }}>
              {vodId ? "Thumbnail loading…" : "No video yet"}
            </span>
          )}
        </div>

        <p className="streamCard__channel">
          Channel: <b>{slugOrId || id}</b>
        </p>

        <button
          className="streamCard__button"
          onClick={() => navigate(`/stream/${slugOrId || id}`)}
        >
          OPEN STREAM
        </button>
      </div>

      <style>{`
        .streamCard{
          width:100%;
          background:rgba(0,0,0,0.45);
          border-radius:20px;
          padding:16px;
          box-shadow:0 0 15px rgba(0,0,0,0.8);
          color:white;
          position:relative;
          min-width:0;
        }

        .streamCard__badge{
          position:absolute;
          top:16px;
          right:16px;
          padding:4px 12px;
          border-radius:999px;
          font-weight:bold;
          font-size:12px;
        }

        .streamCard__badge.live{
          background:red;
        }

        .streamCard__badge.idle{
          background:rgba(255,255,255,0.2);
        }

        .streamCard__title{
          margin:0 0 4px 0;
          font-size:22px;
          line-height:1.15;
        }

        .streamCard__status{
          margin:0 0 10px 0;
          opacity:0.8;
          font-size:13px;
        }

        .streamCard__thumb{
          height:200px;
          border-radius:14px;
          overflow:hidden;
          background:rgba(0,0,0,0.35);
          display:flex;
          align-items:center;
          justify-content:center;
          position:relative;
        }

        .streamCard__thumbImg{
          width:100%;
          height:100%;
          object-fit:cover;
          display:block;
        }

        .streamCard__thumbLabel{
          position:absolute;
          left:12px;
          bottom:12px;
          padding:6px 10px;
          border-radius:999px;
          background:rgba(0,0,0,0.55);
          font-size:12px;
          font-weight:700;
        }

        .streamCard__channel{
          margin-top:12px;
          font-size:13px;
          opacity:0.8;
          word-break:break-word;
        }

        .streamCard__button{
          margin-top:12px;
          width:100%;
          padding:12px;
          border-radius:999px;
          border:none;
          font-weight:bold;
          font-size:15px;
          cursor:pointer;
          background:linear-gradient(90deg, #00c6ff, #ff48c4);
          color:#fff;
        }

        @media (max-width: 640px){
          .streamCard{
            padding:10px;
            border-radius:16px;
          }

          .streamCard__badge{
            top:10px;
            right:10px;
            padding:4px 10px;
            font-size:10px;
          }

          .streamCard__title{
            font-size:15px;
            padding-right:56px;
            display:-webkit-box;
            -webkit-line-clamp:2;
            -webkit-box-orient:vertical;
            overflow:hidden;
            min-height:34px;
          }

          .streamCard__status{
            font-size:11px;
            margin-bottom:8px;
          }

          .streamCard__thumb{
            height:110px;
            border-radius:12px;
          }

          .streamCard__thumbLabel{
            left:8px;
            bottom:8px;
            padding:4px 8px;
            font-size:10px;
          }

          .streamCard__channel{
            margin-top:8px;
            font-size:11px;
          }

          .streamCard__button{
            margin-top:8px;
            padding:10px;
            font-size:13px;
          }
        }
      `}</style>
    </>
  );
}